(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mounted = new WeakMap();

  function mountGallery(gallery) {
    if (mounted.has(gallery)) return;
    const host = gallery.closest('[data-kinora-product]');
    const stage = gallery.querySelector('[data-gallery-stage]');
    const imageButton = gallery.querySelector('[data-open-gallery]');
    const stageImage = gallery.querySelector('[data-gallery-image]');
    const video = gallery.querySelector('[data-gallery-video]');
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!host || !stage || !imageButton || !stageImage || !thumbnails.length) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (node, name, fn, options = {}) => node?.addEventListener(name, fn, { ...options, signal });
    const connection = navigator.connection;
    const counter = gallery.querySelector('[data-gallery-count]');
    const dialog = host.querySelector('[data-gallery-dialog]');
    const largeImage = dialog?.querySelector('[data-lightbox-image]');
    const closeButton = dialog?.querySelector('[data-close-gallery]');
    const initialRect = stage.getBoundingClientRect();
    let inView = initialRect.bottom > 0 && initialRect.top < window.innerHeight && initialRect.right > 0 && initialRect.left < window.innerWidth;
    let current = 0;
    let requested = 0;
    let token = 0;
    let disposed = false;
    let animation = null;
    let idleHandle = null;
    let warmTimer = null;
    let warmedImage = null;
    let playPending = false;
    let playAttempt = 0;
    let gesture = null;
    let suppressZoomUntil = 0;
    const warmedURLs = new Set();
    const typeOf = thumb => thumb?.dataset.galleryType === 'video' ? 'video' : 'image';
    const selectedVideo = () => typeOf(thumbnails[current]) === 'video';
    const constrained = () => reduced.matches || Boolean(connection?.saveData);
    const shouldPlay = () => !disposed && selectedVideo() && requested === current && inView && !document.hidden && !constrained();

    function cancelAnimation() {
      animation?.cancel();
      animation = null;
      stageImage.style.opacity = '1';
      if (video) video.style.opacity = '1';
    }

    function pauseVideo() {
      if (!video) return;
      playAttempt++;
      playPending = false;
      video.autoplay = false;
      video.preload = 'none';
      if (!video.paused) video.pause();
      video.dataset.playing = 'false';
    }

    function clearVideoSource() {
      if (!video?.hasAttribute('src')) return;
      video.removeAttribute('src');
      video.load();
    }

    function resumeVideo() {
      if (!video || !shouldPlay()) { pauseVideo(); return; }
      const source = thumbnails[current].dataset.videoSrc;
      if (!source) return;
      // The video URL is deliberately absent from the player until its slide is selected.
      if (video.getAttribute('src') !== source) {
        video.src = source;
        video.preload = 'auto';
        video.load();
      }
      video.autoplay = true;
      video.preload = 'auto';
      if (playPending || !video.paused) return;
      const attempt = ++playAttempt;
      playPending = true;
      const result = video.play();
      if (!result?.then) { playPending = false; return; }
      result.then(() => {
        if (attempt === playAttempt) playPending = false;
        if (!shouldPlay()) pauseVideo();
        else video.dataset.playing = 'true';
      }).catch(() => {
        if (attempt === playAttempt) playPending = false;
        // A browser that declines autoplay keeps the supplied poster, without extra controls.
      });
    }

    function refreshVideo() {
      if (!video) return;
      if (!shouldPlay()) {
        pauseVideo();
        if (selectedVideo() && constrained()) clearVideoSource();
      } else resumeVideo();
    }

    if (video) {
      video.controls = false;
      video.autoplay = false;
      video.defaultMuted = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      listen(video, 'contextmenu', event => event.preventDefault());
      listen(video, 'loadeddata', refreshVideo);
      listen(video, 'playing', () => { if (!shouldPlay()) pauseVideo(); });
      listen(video, 'error', () => { video.dataset.playing = 'false'; });
    }

    function prepareImage(thumb) {
      const image = new Image();
      image.decoding = 'async';
      image.sizes = stageImage.sizes || '100vw';
      if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
      image.src = thumb.dataset.full;
      return image;
    }

    function cancelWarmup() {
      window.clearTimeout(warmTimer);
      warmTimer = null;
      if (idleHandle !== null && window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
      idleHandle = null;
    }

    function warmAdjacent() {
      idleHandle = null;
      if (disposed || document.hidden || !inView || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      // Only the next image is warmed. Video slides are never prefetched.
      const next = thumbnails[(current + 1) % thumbnails.length];
      if (typeOf(next) !== 'image' || !next.dataset.full || warmedURLs.has(next.dataset.full)) return;
      warmedURLs.add(next.dataset.full);
      warmedImage = prepareImage(next);
    }

    function scheduleWarmup() {
      cancelWarmup();
      if (disposed || document.hidden || !inView) return;
      warmTimer = window.setTimeout(() => {
        warmTimer = null;
        if ('requestIdleCallback' in window) idleHandle = window.requestIdleCallback(warmAdjacent, { timeout: 1800 });
        else warmAdjacent();
      }, 600);
    }

    function updateSelection(index, scrollThumb = true) {
      current = index;
      thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === current)));
      if (counter) counter.textContent = `${current + 1} / ${thumbnails.length}`;
      gallery.dataset.galleryIndex = String(current);
      gallery.dataset.galleryType = typeOf(thumbnails[current]);
      gallery.removeAttribute('aria-busy');
      if (scrollThumb) thumbnails[current].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced.matches ? 'auto' : 'smooth' });
    }

    function reveal(element) {
      if (reduced.matches || !element.animate) return;
      animation = element.animate([{ opacity: .35 }, { opacity: 1 }], { duration: 210, easing: 'ease-out' });
    }

    async function select(index, scrollThumb = true) {
      if (disposed) return;
      const nextIndex = (index + thumbnails.length) % thumbnails.length;
      const target = thumbnails[nextIndex];
      const request = ++token;
      requested = nextIndex;
      cancelAnimation();
      cancelWarmup();
      pauseVideo();
      if (nextIndex === current) {
        updateSelection(current, scrollThumb);
        refreshVideo();
        scheduleWarmup();
        return;
      }
      if (typeOf(target) === 'video' && video) {
        if (dialog?.open) dialog.close();
        if (target.dataset.poster) video.poster = target.dataset.poster;
        imageButton.hidden = true;
        video.hidden = false;
        updateSelection(nextIndex, scrollThumb);
        reveal(video);
        refreshVideo();
        scheduleWarmup();
        return;
      }
      if (!target.dataset.full) { requested = current; refreshVideo(); return; }
      gallery.setAttribute('aria-busy', 'true');
      const image = prepareImage(target);
      try { await image.decode(); } catch {
        if (!disposed && request === token) {
          requested = current;
          gallery.removeAttribute('aria-busy');
          refreshVideo();
        }
        return;
      }
      if (disposed || request !== token) return;
      cancelAnimation();
      if (target.dataset.srcset) stageImage.srcset = target.dataset.srcset;
      else stageImage.removeAttribute('srcset');
      stageImage.src = target.dataset.full;
      stageImage.alt = target.dataset.alt || 'Kinora™ patellar knee strap';
      if (video) video.hidden = true;
      imageButton.hidden = false;
      updateSelection(nextIndex, scrollThumb);
      reveal(stageImage);
      scheduleWarmup();
    }

    thumbnails.forEach((thumb, index) => listen(thumb, 'click', () => select(index)));
    updateSelection(0, false);
    if (stageImage.complete) scheduleWarmup();
    else listen(stageImage, 'load', scheduleWarmup, { once: true });

    listen(stage, 'pointerdown', event => {
      if (!event.isPrimary || event.pointerType === 'mouse') return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { passive: true });
    listen(stage, 'pointerup', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      gesture = null;
      if (Math.abs(dx) < 44 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
      suppressZoomUntil = performance.now() + 600;
      select(requested + (dx < 0 ? 1 : -1));
    }, { passive: true });
    const resetGesture = () => { gesture = null; };
    listen(stage, 'pointercancel', resetGesture, { passive: true });
    listen(stage, 'pointerleave', resetGesture, { passive: true });
    listen(stage, 'dragstart', event => event.preventDefault());
    listen(gallery, 'keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      if (event.target.matches?.('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      const next = (requested + (event.key === 'ArrowRight' ? 1 : -1) + thumbnails.length) % thumbnails.length;
      select(next);
      thumbnails[next].focus({ preventScroll: true });
    });
    listen(imageButton, 'click', event => {
      if (selectedVideo() || (performance.now() < suppressZoomUntil && event.detail !== 0)) { event.preventDefault(); return; }
      if (!dialog || !largeImage || dialog.open) return;
      largeImage.src = thumbnails[current].dataset.full || stageImage.currentSrc || stageImage.src;
      largeImage.alt = stageImage.alt;
      dialog.showModal();
    });
    listen(closeButton, 'click', () => dialog?.close());
    listen(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
    listen(document, 'visibilitychange', () => {
      refreshVideo();
      if (document.hidden) cancelWarmup(); else scheduleWarmup();
    });
    listen(reduced, 'change', () => { cancelAnimation(); refreshVideo(); });
    listen(connection, 'change', refreshVideo);
    listen(window, 'pagehide', pauseVideo);
    listen(window, 'pageshow', refreshVideo);

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        inView = entry.isIntersecting && entry.intersectionRatio >= .15;
        gallery.dataset.galleryInView = String(inView);
        refreshVideo();
        if (inView) scheduleWarmup(); else cancelWarmup();
      }, { threshold: [0, .15, .6] });
      observer.observe(stage);
    }

    mounted.set(gallery, () => {
      disposed = true;
      token++;
      cancelWarmup();
      cancelAnimation();
      pauseVideo();
      controller.abort();
      observer?.disconnect();
      clearVideoSource();
      warmedImage = null;
      if (dialog?.open) dialog.close();
      mounted.delete(gallery);
    });
  }

  function galleriesIn(scope) {
    return scope.matches?.('[data-kinora-gallery]') ? [scope] : [...scope.querySelectorAll('[data-kinora-gallery]')];
  }
  function mount(scope = document) { galleriesIn(scope).forEach(mountGallery); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => galleriesIn(event.target).forEach(gallery => mounted.get(gallery)?.()));
})();
